import React, {useEffect, useState} from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {CSVLink} from "react-csv";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    Grid,
    IconButton,
    Modal,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import {KeyboardDateTimePicker, MuiPickersUtilsProvider,} from '@material-ui/pickers';
import {Autocomplete, Pagination} from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RefreshIcon from '@material-ui/icons/Refresh';
import EmailIcon from '@material-ui/icons/Email';
import DateRangeIcon from '@material-ui/icons/DateRange';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import TrafficIcon from '@material-ui/icons/Traffic';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import RoomServiceIcon from '@material-ui/icons/RoomService';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import ReceiptIcon from '@material-ui/icons/Receipt';
import LaunchIcon from '@material-ui/icons/Launch';
import useStyles from '../styles/styles';
import {useLazyQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {navigate} from '@reach/router';
import useFormInputs from "../hooks/useFormInputs";
import _ from 'lodash';
import setDate from '../utils/setDate';
import dashes from 'add-dashes-to-uuid';
import convertToCurrency from '../utils/convertToCurrency';
import ShortFormLeadsSearchTable from "../components/Leads/ShortFormLeadsSearchTable";

/**
 *
 * @param {*} props
 */
const ShortFormLeads = () => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const classes = useStyles();
    const defaultQuery = {
        status: 'SOLD',
        startDate: startOfDay.toISOString()
    };
    const [variables, setVariables] = useState(defaultQuery);
    const [open, setOpen] = useState(false);
    const [csvData, setCSVData] = useState(false);
    const [paginationVal, setPaginationVal] = useState({
        skipVal: 0,
        currentPage: 1,
        limit: 30,
        numberOfPages: 0,
        totalCount: 0
    });
    const [filterTrafficSources, setFilterTrafficSources] = useState([]);
    const [activeColumns, setActiveColumns] = useState(ShortFormLeadsSearchTable());
    const [configColumns, setConfigColumns] = useState(activeColumns);
    const onSubmit = () => {
        const finalInputs = _.omitBy(inputs, _.isEmpty);
        if (_.isEqual(variables, finalInputs)) {
            console.log("refectching")
        }
        const startDate = finalInputs.startDate === undefined ? startOfDay : finalInputs.startDate;
        let status = inputs.status;
        if (status === undefined) {
            status = 'SOLD';
        } else if (status === '') {
            status = ['SOLD', 'UNSOLD'];
        }

        setPaginationVal({
            ...paginationVal,
            limit: 30,
            skipVal: 0
        });
        if (_.has(finalInputs, 'search')) {
            const searchId = finalInputs.search.includes('-') ? finalInputs.search : dashes(finalInputs.search);
            navigate(`/info/leads/${searchId}`);
        } else {
            setVariables({
                ...finalInputs,
                startDate,
                status,
            });
        }
    };
    const {
        clearInput,
        clearAllInputs,
        inputs,
        setInputs,
        handleFilterChange,
        handleInputChange,
        handleSubmit
    } = useFormInputs(onSubmit);

    const [load, {loading, error, data, refetch, networkStatus}] = useLazyQuery(gql`
        query AllLeads(
            $input: AllApiRequestInput!
        ) {
            allApiRequest(
                input: $input
            ) {
                totalCount
                nodes {
                    id
                    requestId
                    createdAt
                    firstName
                    lastName
                    minimumPrice
                    primaryEmail
                    primaryPhone
                    homeState
                    homeZip
                    referringSiteUrl
                    bestCallTime
                    clientIpAddress
                    userAgent
                    trafficSource {
                        id
                        name
                        legacyPromoId
                        isDeleted
                        affiliate {
                            id
                            name
                        }
                    }
                    loanRedirects {
                        id
                        destinationUrl
                    }
                    apiResult {
                        entityId
                        campaign
                        status
                        servicer
                        portfolio
                        billed
                        payout
                    }
                    apiResponse {
                        entityId
                        status
                    }
                }
                allServicers {
                    id
                    name
                }
                allAffiliates {
                    id
                    name
                }
                allTrafficSources {
                    id
                    name
                    legacyPromoId
                    isDeleted
                    affiliate {
                        id
                    }
                }
                allCampaigns {
                    id
                    name
                }
            }
        }
    `, {
        variables: {
            input: {
                email: variables.email,
                startDate: variables.startDate,
                endDate: variables.endDate,
                servicerId: variables.soldServicerId ?? variables.postServicerId,
                status: variables.status,
                campaign: variables.soldCampaignId ?? variables.postCampaignId,
                affiliateId: variables.affiliateId,
                trafficSourceId: variables.trafficSourceId,
                everflowTransactionId: variables.everflowTransactionId,
                limit: paginationVal.limit,
                skip: paginationVal.skipVal
            },
        },
        context: {
            clientName: "kgraphql"
        },
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        load()
    }, [])

    const allAffiliates = (data && data.allApiRequest.allAffiliates && data.allApiRequest.allAffiliates) || [];
    const allServicers = (data && data.allApiRequest.allServicers && data.allApiRequest.allServicers.sort((a, b) => (a.name > b.name) ? 1 : -1)) || [];
    const allTrafficSources = (data && data.allApiRequest.allTrafficSources && data.allApiRequest.allTrafficSources) || [];
    const allCampaigns = (data && data.allApiRequest.allCampaigns && data.allApiRequest.allCampaigns) || [];
    const totalCount = (data && data.allApiRequest && data.allApiRequest.totalCount) || 0;
    const numberOfPages = totalCount > 0 ? Math.ceil(totalCount / paginationVal.limit) : 0;

    const handleClose = () => {
        setOpen(false);
    };

    const handleCSVData = () => {
        let dataArr = {
            headers: [{
                label: 'Status',
                key: 'status'
            }, {
                label: 'Created',
                key: 'created'
            },
                {
                    label: 'Primary Email',
                    key: 'primaryEmail'
                },
                {
                    label: 'Affiliate',
                    key: 'affiliate'
                },
                {
                    label: 'Min',
                    key: 'min'
                },
                {
                    label: 'Billed',
                    key: 'billed'
                },
                {
                    label: 'Payout',
                    key: 'payout'
                },
                {
                    label: 'Traffic Source',
                    key: 'trafficSource'
                }, {
                    label: 'Servicer',
                    key: 'servicer'
                }, {
                    label: 'Campaign',
                    key: 'campaign'
                }],
            data: []
        };
        data.allApiRequest.nodes.forEach(item => {
            dataArr.data.push({
                status: item.apiResponse && item.apiResponse.status,
                created: setDate(item.createdAt),
                primaryEmail: item.primaryEmail,
                affiliate: item.trafficSource.affiliate.name,
                min: convertToCurrency(item.minimumPrice),
                billed: item.apiResult && item.apiResult.billed && convertToCurrency(item.apiResult.billed),
                payout: item.apiResult && item.apiResult.payout && convertToCurrency(item.apiResult.payout),
                trafficSource: `${item.trafficSource.name} (${item.trafficSource.legacyPromoId})`,
                servicer: item.apiResult && item.apiResult.servicer,
                campaign: item.apiResult && item.apiResult.campaign
            });
        });
        if (dataArr.data.length) {
            setCSVData(dataArr);
        }
    };

    useEffect(() => {
        if (data && Object.keys(data).length) {
            if (inputs.startDate === undefined) {
                setInputs({
                    status: 'SOLD',
                    startDate: startOfDay.toISOString()
                });
            }
            setPaginationVal(p => ({
                ...p,
                totalCount,
                numberOfPages: numberOfPages === 0 ? 1 : numberOfPages
            }));
            handleCSVData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (inputs) {
            if (inputs.affiliateId) {
                const arr = allTrafficSources.filter(item => item.affiliate.id === inputs.affiliateId);
                setFilterTrafficSources(arr);
            } else {
                setFilterTrafficSources([]);
            }
        }
    }, [inputs])

    /**
     * @param {array} options - array of options for input
     * @param {string} searchKey - value to search on
     * @param {string} keyVal - key value to reference
     * @param {array} optionKeys - key values to return
     */
    const getActiveVal = (options, searchKey, keyVal, optionKeys) => {
        const item = options.filter(item => searchKey ? item[searchKey] === inputs[keyVal] : item === inputs[keyVal]);
        if (item.length) {
            let optionLabel = "";
            if (optionKeys.length) {
                optionKeys.forEach(key => {
                    if (!optionLabel.length) {
                        optionLabel = item[0][key];
                    } else {
                        optionLabel = `${optionLabel} - ${item[0][key]}`
                    }
                });
            } else {
                optionLabel = item[0];
            }
            return optionLabel;
        }
        return null;
    };

    const AutoCompleteInput = ({options, searchKey, keyVal, label, optionKeys}) => {
        return (
            <Autocomplete
                className={classes.autoComplete}
                value={inputs[keyVal] || null}
                onChange={(event, value) => handleFilterChange(searchKey ? (value ? value[searchKey] : '') : (value ? value.replace(/['"]+/g, '') : ''), keyVal)}
                options={options}
                renderOption={option => {
                    let optionLabel = "";
                    if (optionKeys.length) {
                        optionKeys.forEach(key => {
                            if (!optionLabel.length) {
                                optionLabel = option[key];
                            } else {
                                optionLabel = `${optionLabel} - ${option[key]}`
                            }
                        });
                    } else {
                        optionLabel = option;
                    }
                    return optionLabel;
                }}
                getOptionLabel={option => {
                    const activeVal = getActiveVal(options, searchKey, keyVal, optionKeys);
                    return activeVal || (_.has(option, 'name') ? option.name : option);
                }}
                renderInput={params => <TextField {...params} label={label} fullWidth/>}
            />
        );
    };

    const LoanRequestsTable = () => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Action</TableCell>
                        {activeColumns.map(column => (
                            column.enabled &&
                            <TableCell
                                key={column.id}
                                variant="head"
                                align={column.alignment ? column.alignment : 'inherit'}
                            >
                                {column.title}
                            </TableCell>

                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading || networkStatus === 4 ? (
                        <TableRow key="graphql-loading">
                            <TableCell colSpan={12} style={{textAlign: "center"}}>
                                Loading…
                                <br/>
                                <CircularProgress
                                    size={128}
                                    thickness={4}
                                />
                            </TableCell>
                        </TableRow>
                    ) : (error ? (
                        <TableRow key="graphql-error">
                            <TableCell colSpan={12}>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </TableCell>
                        </TableRow>
                    ) : (data && data.allApiRequest.nodes.map((loanRequest) => (
                        <TableRow key={loanRequest.id}>
                            <>
                                <TableCell variant="body">
                                    <div>
                                        <LaunchIcon
                                            onClick={() => window.open(`/info/leads/short/${loanRequest.requestId}`)}
                                        />
                                    </div>
                                </TableCell>
                                {activeColumns.map(column => (
                                    column.enabled &&
                                    <TableCell
                                        key={column.id}
                                        variant="body"
                                        align={column.alignment ? column.alignment : 'inherit'}
                                    >
                                        {column.data(loanRequest)}
                                    </TableCell>
                                ))}
                            </>
                        </TableRow>
                    ))))}
                </TableBody>
            </Table>
        );
    };
    return (
        <>
            <Typography variant="h2">
                Leads
            </Typography>
            <Container maxWidth={false} className={classes.chips}>
                {inputs.email ? (
                    <Chip
                        icon={<EmailIcon/>}
                        label="Email"
                        clickable
                        onClick={() => clearInput('email')}
                        onDelete={() => clearInput('email')}
                    />
                ) : null}
                {inputs.startDate || inputs.endDate ? (
                    <Chip
                        icon={<DateRangeIcon/>}
                        label="Date Range"
                        clickable
                        onClick={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                        onDelete={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                    />
                ) : null}
                {inputs.affiliateId ? (
                    <Chip
                        icon={<SupervisorAccountIcon/>}
                        label="Affilates"
                        clickable
                        onClick={() => clearInput('affiliateId')}
                        onDelete={() => clearInput('affiliateId')}
                    />
                ) : null}
                {inputs.trafficSourceId ? (
                    <Chip
                        icon={<TrafficIcon/>}
                        label="Traffic Sources"
                        clickable
                        onClick={() => clearInput('trafficSourceId')}
                        onDelete={() => clearInput('trafficSourceId')}
                    />
                ) : null}
                {inputs.soldServicerId ? (
                    <Chip
                        icon={<AccountBalanceIcon/>}
                        label="Servicers"
                        color="primary"
                        clickable
                        onClick={() => clearInput('soldServicerId')}
                        onDelete={() => clearInput('soldServicerId')}
                    />
                ) : null}
                {inputs.soldCampaignId ? (
                    <Chip
                        icon={<RoomServiceIcon/>}
                        label="Campaigns"
                        color="primary"
                        clickable
                        onClick={() => clearInput('soldCampaignId')}
                        onDelete={() => clearInput('soldCampaignId')}
                    />
                ) : null}
                {inputs.status ? (
                    <Chip
                        icon={<DonutLargeIcon/>}
                        label="Status"
                        color="primary"
                        clickable
                        onClick={() => clearInput('status')}
                        onDelete={() => clearInput('status')}
                    />
                ) : null}
                {inputs.postServicerId ? (
                    <Chip
                        icon={<AccountBalanceIcon/>}
                        label="Servicers"
                        color="secondary"
                        clickable
                        onClick={() => clearInput('postServicerId')}
                        onDelete={() => clearInput('postServicerId')}
                    />
                ) : null}
                {inputs.postCampaignId ? (
                    <Chip
                        icon={<RoomServiceIcon/>}
                        label="Campaigns"
                        color="secondary"
                        clickable
                        onClick={() => clearInput('postCampaignId')}
                        onDelete={() => clearInput('postCampaignId')}
                    />
                ) : null}
                {inputs.everflowTransactionId ? (
                    <Chip
                        icon={<ReceiptIcon/>}
                        label="Everflow Transaction Id"
                        color="primary"
                        clickable
                        onClick={() => clearInput('everflowTransactionId')}
                        onDelete={() => clearInput('everflowTransactionId')}
                    />
                ) : null}
            </Container>
            <form id="leads-form" onSubmit={handleSubmit}>
                <ExpansionPanel expanded>
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h5">Filter Leads</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.filter}>
                        <Container maxWidth={false}>
                            <Box className={classes.filterBox} style={{marginTop: 0}}>
                                <Chip
                                    label="Default Options"
                                    className={classes.filterSectionLabels}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            id="search"
                                            name="search"
                                            label="Search by UUID"
                                            value={inputs.search}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            id="everflowTransactionId"
                                            name="everflowTransactionId"
                                            label="Search by Everflow Transaction Id"
                                            value={inputs.everflowTransactionId}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            id="email"
                                            name="email"
                                            label="Email"
                                            value={inputs.email || ''}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDateTimePicker
                                                disableFuture
                                                ampm={false}
                                                variant="inline"
                                                margin="normal"
                                                id="date-picker-start"
                                                label="Start Date"
                                                value={inputs.startDate || null}
                                                onChange={date => handleFilterChange(date.toISOString(), 'startDate')}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                className={classes.formControl}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDateTimePicker
                                                disableFuture
                                                ampm={false}
                                                variant="inline"
                                                margin="normal"
                                                id="date-picker-end"
                                                label="End Date"
                                                value={inputs.endDate || null}
                                                onChange={date => handleFilterChange(date.toISOString(), 'endDate')}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                className={classes.formControl}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <AutoCompleteInput
                                            options={allAffiliates}
                                            searchKey="id"
                                            keyVal="affiliateId"
                                            label="Affiliates"
                                            optionKeys={["name"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <AutoCompleteInput
                                            options={filterTrafficSources.length ? filterTrafficSources : allTrafficSources}
                                            searchKey="id"
                                            keyVal="trafficSourceId"
                                            label="Traffic Sources"
                                            optionKeys={['name', 'legacyPromoId']}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                        <Container maxWidth={false}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <Box className={classes.filterBox}>
                                        <Chip
                                            label="Sold Options"
                                            className={classes.filterSectionLabels}
                                            color="primary"
                                        />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <AutoCompleteInput
                                                    options={allServicers}
                                                    searchKey="id"
                                                    keyVal="soldServicerId"
                                                    label="Servicers"
                                                    optionKeys={['name']}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <AutoCompleteInput
                                                    options={allCampaigns}
                                                    searchKey="id"
                                                    keyVal="soldCampaignId"
                                                    label="Campaigns"
                                                    optionKeys={['id']}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <AutoCompleteInput
                                                    options={['SOLD', 'UNSOLD']}
                                                    searchKey=""
                                                    keyVal="status"
                                                    label="Status"
                                                    optionKeys={[]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>

                                </Grid>
                                {/* <Grid item xs={12} md={5}>
                                    <Box className={classes.filterBox}>
                                        <Chip
                                            label="Posted Options"
                                            className={classes.filterSectionLabels}
                                            color="secondary"
                                        />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <AutoCompleteInput
                                                    options={allServicers}
                                                    searchKey="id"
                                                    keyVal="postServicerId"
                                                    label="Servicers"
                                                    optionKeys={['name']}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <AutoCompleteInput
                                                    options={allCampaigns}
                                                    searchKey="id"
                                                    keyVal="postCampaignId"
                                                    label="Campaigns"
                                                    optionKeys={['id']}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid> */}
                            </Grid>
                        </Container>
                        <Box paddingTop={3}>
                            <Grid container spacing={2} justify="center" alignItems="center">
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Button type="submit" variant="contained" color="primary" fullWidth>
                                        Filter Results
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Button onClick={() => {
                                        clearAllInputs();
                                        setVariables(defaultQuery);
                                    }} variant="outlined" color="secondary" fullWidth>
                                        Clear Filters
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </form>

            <Paper className={classes.paper}>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justify="flex-end"
                >
                    <Button onClick={() => {
                        setConfigColumns(activeColumns);
                        setOpen(true);
                    }} variant="contained" color="secondary">
                        Select Table Columns
                    </Button>
                    <IconButton aria-label="refetch results" onClick={() => refetch()}>
                        <RefreshIcon/>
                    </IconButton>
                    {csvData ? (

                        <CSVLink
                            data={csvData.data}
                            headers={csvData.headers}
                            style={{textDecoration: 'none'}}
                        >
                            <Button variant="contained" color="secondary">
                                Export Current Page
                            </Button>
                        </CSVLink>
                    ) : null}
                </Grid>
                <LoanRequestsTable/>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justify="space-between"
                    className={classes.paginationContainer}
                >
                    <Grid item xs>
                        <Typography variant="body2">
                            Displaying {Math.min(paginationVal.totalCount, paginationVal.skipVal + 1)}-{Math.min(paginationVal.skipVal + paginationVal.limit, paginationVal.totalCount)} of {paginationVal.totalCount} Results
                        </Typography>
                    </Grid>
                    <Grid item xs>
                        {paginationVal.numberOfPages > 1 ? (
                            <Pagination
                                count={paginationVal.numberOfPages}
                                showFirstButton
                                showLastButton
                                color="primary"
                                onChange={(e, value) => {
                                    setPaginationVal({
                                        ...paginationVal,
                                        currentPage: value,
                                        skipVal: paginationVal.limit * (value - 1)
                                    });
                                }}
                                page={paginationVal.currentPage}
                            />
                        ) : null}
                    </Grid>
                </Grid>
                {open && (
                    <Modal
                        open={open}
                        onClose={handleClose}
                    >
                        <Container className={classes.modal} maxWidth={false}>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Typography variant={"h6"}>Select Table Columns</Typography>
                                <Grid
                                    container
                                    spacing={3}
                                    direction="row"
                                    alignItems="center"
                                >
                                    {activeColumns.map((column, idx) => (
                                        <Grid item xs={3} key={column.id}>
                                            <Typography
                                                variant={"body1"}><strong>{column.title}</strong></Typography>
                                            <Switch
                                                onChange={(e) => {
                                                    setConfigColumns(configColumns.map(item => (item.id === column.id ? Object.assign({}, item, {
                                                        ...item,
                                                        enabled: e.target.checked
                                                    }) : item)))
                                                }}
                                                checked={configColumns[idx].enabled}
                                                name={column.title}
                                                inputProps={{'aria-label': 'primary checkbox'}}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box
                                    display={"flex"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                >
                                    <Button
                                        onClick={() => {
                                            setActiveColumns(configColumns);
                                            setOpen(false);
                                        }}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Paper>
                        </Container>
                    </Modal>
                )}
            </Paper>
        </>
    );
};

export default ShortFormLeads;
